import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import DossierSlide, { LAYOUT_OPTIONS, LAYOUT_LABELS, resolveAuto } from "@/components/dossier/DossierSlide";
import { paginateText, layoutCapacity } from "@/components/dossier/dossier_pagination";

/**
 * DossierPresentation · vista 16:9 navegable con plantillas editoriales.
 *
 * Reescrito para C7. Las secciones ya no se renderizan con un layout único
 * y plano: cada slide elige una de 9 plantillas (Portada, Cita, Texto solo,
 * Dos columnas, Texto+imagen izq/der, Grid 2×2, Imagen a sangre, Fichas).
 *
 * Además:
 * - Slide 1 es siempre una PORTADA automática (proyecto + director + 1ª imagen
 *   disponible) aunque no exista una sección "portada".
 * - Selector live: durante la presentación se puede cambiar la plantilla del
 *   slide actual y se persiste en MongoDB vía PUT /icaa/projects/{id} (campo
 *   `section.layout`). Este mismo campo lo respeta el export PPTX y el
 *   editor del dossier.
 * - Navegación con teclado: ← → flecha, Esc / espacio, Cmd+L abre selector.
 *
 * NO modifica nada del editor de guion.
 */
export default function DossierPresentation({ project, onClose, onSectionUpdate }) {
  // ----- Slides = portada virtual + secciones con contenido -----
  const slides = useMemo(() => buildSlideList(project), [project]);
  const [idx, setIdx] = useState(0);
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);
  // Persistencia simple en localStorage — el usuario decide si quiere la marca
  // de agua "GRABARTE" en el footer de los slides y en el chrome.
  const [watermarkOn, setWatermarkOn] = useState(() => {
    try { return localStorage.getItem("dossier-watermark") !== "off"; }
    catch { return true; }
  });
  const toggleWatermark = () => {
    setWatermarkOn((v) => {
      const next = !v;
      try { localStorage.setItem("dossier-watermark", next ? "on" : "off"); } catch {}
      return next;
    });
  };
  const slide = slides[idx];

  const next = () => setIdx((i) => Math.min(slides.length - 1, i + 1));
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  useEffect(() => {
    const handler = (e) => {
      // si está abierto el picker, dejar que el select capture las teclas
      if (layoutPickerOpen) return;
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "l" || e.key === "L") setLayoutPickerOpen(true);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, slides.length, layoutPickerOpen]);

  // ----- Cambiar layout del slide actual + persistir -----
  const changeLayout = async (newLayout) => {
    if (!slide || slide.kind !== "section") return;
    const sections = project?.dossier?.sections || [];
    const nextSections = sections.map((s) =>
      s.key === slide.section.key ? { ...s, layout: newLayout } : s,
    );
    const nextProject = { ...project, dossier: { ...(project.dossier || {}), sections: nextSections } };
    // Optimistic update vía parent
    if (typeof onSectionUpdate === "function") onSectionUpdate(nextProject);
    setLayoutPickerOpen(false);
    // Persistir (silencioso — el editor del dossier ya usa el mismo patrón)
    try {
      await api.put(`/icaa/projects/${project.id}`, nextProject);
    } catch (e) {
      console.error("[DossierPresentation] No se pudo guardar el layout:", e);
    }
  };

  if (slides.length === 0) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gb-black/95 p-6 text-white"
           data-testid="dossier-presentation">
        <div className="max-w-md text-center">
          <p className="gb-eyebrow text-gb-red">Modo presentación</p>
          <h2 className="mt-2 gb-display text-3xl">Dossier vacío</h2>
          <p className="mt-3 text-sm text-white/65">
            Rellena al menos una sección del dossier para poder enseñarlo en pantalla completa.
          </p>
          <button onClick={onClose}
                  className="mt-6 rounded-full bg-white px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-black hover:bg-gb-red hover:text-white">
            Volver
          </button>
        </div>
      </div>
    );
  }

  // Layout efectivo del slide actual. Migramos "magazine-spread" (retirado
  // en Feb 2026) a "text-image-right" para que el pill muestre el nombre
  // correcto y el LayoutPicker no señale una plantilla inexistente.
  const rawLayout = slide?.kind === "section"
    ? (slide.section.layout || resolveAuto(slide.section))
    : "cover";
  const currentLayout = rawLayout === "magazine-spread" ? "text-image-right" : rawLayout;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-gb-black text-white"
         data-testid="dossier-presentation">
      {/* Top bar */}
      <header className="flex items-center justify-between border-b border-white/10 px-6 py-3">
        <div className="flex items-baseline gap-3 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/45">
            {project.title}
          </p>
          <span className="text-white/30">·</span>
          <p className="truncate text-[11px] uppercase tracking-[0.22em] text-white/70">
            Slide {idx + 1} / {slides.length}
            {slide?.kind === "section" && (
              <>
                <span className="mx-2 text-white/30">·</span>
                <span className="text-white/55">
                  {slide.section.label}
                  {slide.part && (
                    <span className="ml-1 text-gb-red"> · {slide.part.index} de {slide.part.total}</span>
                  )}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleWatermark}
            data-testid="dossier-presentation-watermark-toggle"
            title={watermarkOn ? "Quitar marca de agua (Grabarte)" : "Mostrar marca de agua (Grabarte)"}
            className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] transition ${
              watermarkOn
                ? "border-white/30 bg-white/10 text-white hover:border-gb-red hover:text-gb-red"
                : "border-white/15 text-white/45 hover:border-white/30 hover:text-white/70"
            }`}>
            ⌖ Marca de agua · {watermarkOn ? "ON" : "OFF"}
          </button>
          {slide?.kind === "section" && (
            <button
              onClick={() => setLayoutPickerOpen((v) => !v)}
              data-testid="dossier-presentation-layout"
              title="Cambiar plantilla (L)"
              className="rounded-full border border-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/65 transition hover:border-gb-red hover:text-gb-red">
              ⚏ Plantilla · {LAYOUT_LABELS[currentLayout] || currentLayout}
            </button>
          )}
          <button onClick={onClose} data-testid="dossier-presentation-close"
                  className="rounded-full border border-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/65 hover:border-gb-red hover:text-gb-red"
                  title="Cerrar (Esc)">
            ✕ Cerrar
          </button>
        </div>
      </header>

      {/* Layout picker overlay */}
      {layoutPickerOpen && slide?.kind === "section" && (
        <LayoutPickerOverlay
          section={slide.section}
          project={project}
          current={slide.section.layout || ""}
          onPick={changeLayout}
          onClose={() => setLayoutPickerOpen(false)}
        />
      )}

      {/* Slide stage */}
      <div className="flex flex-1 items-center justify-center px-6 py-6">
        <div className="relative w-full max-w-6xl" style={{ aspectRatio: "16 / 9", containerType: "inline-size" }}>
          {/* Prev arrow */}
          {idx > 0 && (
            <button onClick={prev} data-testid="dossier-presentation-prev"
                    className="absolute -left-14 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-white/55 transition hover:border-gb-red hover:text-gb-red"
                    title="Anterior (←)">‹</button>
          )}

          {/* Slide content */}
          <div className="absolute inset-0 overflow-hidden rounded-2xl bg-gb-paper-soft text-gb-black shadow-2xl"
               data-testid={`dossier-slide-${slide.id}`}>
            {slide.kind === "cover" ? (
              <DossierSlide
                section={slide.section}
                project={project}
                layout="cover"
                slideIndex={idx + 1}
                slideTotal={slides.length}
                showWatermark={watermarkOn}
              />
            ) : (
              <DossierSlide
                section={slide.section}
                project={project}
                layout={slide.section.layout || ""}
                slideIndex={idx + 1}
                slideTotal={slides.length}
                showWatermark={watermarkOn}
                part={slide.part}
              />
            )}
          </div>

          {/* Next arrow */}
          {idx < slides.length - 1 && (
            <button onClick={next} data-testid="dossier-presentation-next"
                    className="absolute -right-14 top-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-white/55 transition hover:border-gb-red hover:text-gb-red"
                    title="Siguiente (→)">›</button>
          )}
        </div>
      </div>

      {/* Thumbnails strip */}
      <footer className="border-t border-white/10 px-6 py-3">
        <div className="flex gap-1.5 overflow-x-auto">
          {slides.map((s, j) => (
            <button key={s.id} onClick={() => setIdx(j)} data-testid={`dossier-thumb-${s.id}`}
                    className={`shrink-0 rounded px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition ${
                      j === idx
                        ? "bg-gb-red text-white"
                        : "border border-white/10 text-white/55 hover:border-white/30 hover:text-white"
                    }`}>
              {j + 1}. {s.kind === "cover" ? "Portada" : (s.section.label || s.section.key)}
              {s.part && <span className="ml-1 opacity-70">· {s.part.index}/{s.part.total}</span>}
            </button>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/35">
            ← / → navegar · L plantilla · Esc cerrar
          </p>
          {watermarkOn && (
            <div className="flex items-center gap-2 opacity-70">
              <span className="text-[9px] uppercase tracking-[0.3em] text-white/35">
                Producido por
              </span>
              <img src="/brand/logo-white.png" alt="grabarte" className="h-3.5 w-auto" />
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

// =================================================================
// Build slide list: virtual cover (slide 1) + secciones con contenido
// =================================================================
function buildSlideList(project) {
  const sections = (project?.dossier?.sections || []).filter(
    (s) => (s.text && s.text.trim()) || (s.items || []).length > 0 || (s.images || []).length > 0,
  );
  if (sections.length === 0) return [];

  // ¿Existe ya una sección "portada" o "titulo" con contenido?
  const hasExplicitCover = sections.some((s) => s.key === "portada" || s.key === "titulo");

  const slides = [];

  // Slide 1: portada virtual (solo si NO hay portada explícita)
  if (!hasExplicitCover) {
    // Coger primera imagen disponible de cualquier sección
    const firstImage = sections.flatMap((s) => s.images || [])[0] || null;
    const eyebrowText = (project?.logline || project?.synopsis_short || "").trim();
    slides.push({
      id: "__virtual_cover__",
      kind: "cover",
      section: {
        key: "__virtual_cover__",
        label: "Portada",
        text: eyebrowText,
        images: firstImage ? [firstImage] : [],
        items: [],
      },
    });
  }

  // Resto de secciones — con auto-paginación si el texto es largo
  sections.forEach((s) => {
    const text = s.text || "";
    const userLayout = s.layout || "";

    // Decisión 1: ¿necesita paginación?
    // Para evaluar, usamos un layout "page-friendly" si el usuario no fijó uno,
    // o el suyo si tiene buena capacidad. Si el suyo tiene poca capacidad pero
    // el texto es muy largo, lo sobreescribimos para que el dossier sea legible.
    const initialLayout = userLayout || resolveAuto(s);
    let pagingLayout = initialLayout;
    const initialCap = layoutCapacity(initialLayout);

    if (text.length > 700 && initialCap > 0 && initialCap < 700) {
      // Layout demasiado pequeño para tanto texto — promoción automática
      const hasImg = (s.images || []).length > 0;
      pagingLayout = hasImg ? "text-image-right" : "two-cols";
    }

    const capacity = layoutCapacity(pagingLayout);

    if (!capacity || text.length <= capacity) {
      // No requiere paginación — 1 slide normal con el layout original
      slides.push({ id: s.key, kind: "section", section: s });
      return;
    }

    // Paginar el texto en N partes y generar N slides
    const parts = paginateText(text, capacity);
    parts.forEach((partText, partIdx) => {
      const partSection = {
        ...s,
        text: partText,
        images: partIdx === 0 ? s.images : [],
        items: partIdx === 0 ? s.items : [],
      };
      // Layout consistente para todas las partes (page-friendly).
      // Parte 1 puede usar el layout con imagen; partes 2+ usan layout sin imagen.
      let partLayout;
      if (partIdx === 0) {
        partLayout = pagingLayout;
      } else {
        // Las partes 2+ no tienen imagen → text-narrow o two-cols
        partLayout = partText.length > 800 ? "two-cols" : "text-narrow";
      }
      slides.push({
        id: parts.length > 1 ? `${s.key}__${partIdx + 1}` : s.key,
        kind: "section",
        section: { ...partSection, layout: partLayout },
        part: parts.length > 1 ? { index: partIdx + 1, total: parts.length } : null,
      });
    });
  });

  return slides;
}

// =================================================================
// LayoutPickerOverlay · grid 3×3 con miniaturas vivas de cada plantilla
// =================================================================
function LayoutPickerOverlay({ section, project, current, onPick, onClose }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gb-black/85 backdrop-blur-sm"
         data-testid="dossier-layout-picker"
         onClick={onClose}>
      <div className="w-full max-w-5xl px-8 py-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/45">
              Elige plantilla para
            </p>
            <h3 className="mt-0.5 gb-display text-2xl text-white">{section.label}</h3>
          </div>
          <button onClick={onClose} data-testid="dossier-layout-picker-close"
                  className="rounded-full border border-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/65 hover:border-gb-red hover:text-gb-red">
            ✕ Cancelar
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-4">
          {LAYOUT_OPTIONS.map((opt) => {
            const isCurrent = (opt.value || "") === (current || "");
            return (
              <button
                key={opt.value || "auto"}
                onClick={() => onPick(opt.value)}
                data-testid={`dossier-layout-opt-${opt.value || "auto"}`}
                className={`group overflow-hidden rounded-lg border text-left transition ${
                  isCurrent
                    ? "border-gb-red ring-2 ring-gb-red"
                    : "border-white/15 hover:border-white/40"
                }`}>
                <div className="relative w-full bg-[#FAFAF6]" style={{ aspectRatio: "16 / 9", containerType: "inline-size" }}>
                  <div className="absolute inset-0">
                    <DossierSlide
                      section={section}
                      project={project}
                      layout={opt.value || ""}
                    />
                  </div>
                </div>
                <div className={`flex items-center justify-between px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] ${
                  isCurrent ? "bg-gb-red text-white" : "bg-white/5 text-white/65"
                }`}>
                  <span>{opt.label}</span>
                  {isCurrent && <span>✓</span>}
                </div>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-center text-[10px] uppercase tracking-[0.22em] text-white/35">
          La elección se guarda en la sección y también se aplica al exportar a PPTX.
        </p>
      </div>
    </div>
  );
}
